import { Chip, Stack } from "@mui/material";
import { chain, debounce, throttle } from "lodash-es";
import { ReactNode, useCallback, useEffect } from "react";

import ListViewLayout from "@/App/Layouts/ListViewLayout";
import SimpleViewPageHeader from "@/App/Layouts/PageHeader/SimpleViewPageHeader";
import TableCellContentOfCount from "@/common/components/Table/TableCell/TableCellContentOfCount";
import { FeatureName } from "@/common/constants/featureName";
import { ROUTE_PATH } from "@/common/constants/routing";
import { FilterFieldOperatorSpec } from "@/common/filters/filterFieldOperatorSpec";
import { TagStaticFilterInputDto, TagsHelper } from "@/common/helpers/entity/tags";
import { getTypedPath } from "@/common/helpers/typedPath";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCommonRequestParams } from "@/common/hooks/api/useCommonRequestParams";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { useLatestRef } from "@/common/hooks/ref/useLatestRef";
import { PropagatedDeps } from "@/common/hooks/render/usePropagatedDeps";
import { chatHubService } from "@/common/realtime/chatHubService";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ApiEnumName } from "@/common/services/enum";
import { FilterFieldType } from "@/common/ts/filters";
import { ViewVariant } from "@/common/ts/layout";
import { featureManager } from "@/config/features";
import { apiClient } from "@/core/api/ApiClient";
import {
  ChatDto,
  ChatGetPaginatedDto,
  ChatType,
  DataUpdatesHubClientMethodName,
  EntityType,
  FilterOperator,
  FilterType,
  FilterValueType,
  GeneralAttachedTagsInputDto,
} from "@/core/api/generated/v1";

import DataTabular from "../../../DataTabular/DataTabular";
import Datetime from "../../../Datetime/Datetime";
import InlineApiEnumValue from "../../../Enum/InlineApiEnumValue";
import FilterArrayOfEnumValueInput from "../../../Filters/ValueInputs/FilterArrayOfEnumValueInput";
import GeneralScopeLink from "../../../Link/GeneralScopeLink";
import AppTypography from "../../../Text/AppTypography";
import AssignmentAutocomplete from "../../Assignment/AssignmentAutocomplete";
import { ChatMessageCountInTableCell } from "../../ChatMessage/ChatMessageCountInTableCell";
import ChatParticipantAvatar from "../../ChatParticipant/ChatParticipantAvatar";
import ContractAutocomplete from "../../Contract/ContractAutocomplete";
import DamageCostEvaluationAutocomplete from "../../DamageCostEvaluation/DamageCostEvaluationAutocomplete";
import DamageDetectionAutocomplete from "../../DamageDetection/DamageDetectionAutocomplete";
import MaintenanceAutocomplete from "../../Maintenance/MaintenanceAutocomplete";
import RepairOperationAutocomplete from "../../RepairOperation/RepairOperationAutocomplete";
import VehicleAutocomplete from "../../Vehicle/VehicleAutocomplete";
import WashAutocomplete from "../../Wash/WashAutocomplete";
import WheelOperationAutocomplete from "../../WheelOperation/WheelOperationAutocomplete";

export enum InternalChatColumns {
  Vehicle = "__vehicle__",
  Contract = "__contract__",
  Assignment = "__assignment__",
  DamageDetection = "__damageDetection__",
  DamageCostEvaluation = "__damageCostEvaluation__",
  RepairOperation = "__repairOperation__",
  Wash = "__wash__",
  Maintenance = "__maintenance__",
  WheelOperation = "__wheelOperation__",
}

const displayColumnProps = {
  id: true,
  globalNumber: true,
  localNumber: true,
  vehicle: true,
  contract: true,
  assignment: true,
  damageDetection: true,
  damageCostEvaluation: true,
  repairOperation: true,
  wash: true,
  maintenance: true,
  wheelOperation: true,
};

export const defaultDisplayProps = {
  viewVariant: ViewVariant.Page,
  breadcrumbs: true,
  header: true,
  filters: true,
  columns: displayColumnProps as Partial<typeof displayColumnProps>,
};

export type DisplayProps = typeof defaultDisplayProps;

export const inboxColumns: Partial<typeof displayColumnProps> = {
  vehicle: false,
  contract: false,
  assignment: false,
  damageDetection: false,
  damageCostEvaluation: false,
  repairOperation: false,
  wash: false,
  maintenance: false,
  wheelOperation: false,
};

export const entityInboxColumns: Partial<typeof displayColumnProps> = inboxColumns;

const defaultPropagatedDeps = {
  refetch: {},
};

export type ChatPaginatedListPropagatedDepsMap = typeof defaultPropagatedDeps;

export interface ChatPaginatedListOwnProps {
  persistenceKey?: string;
  defaultValues?: Omit<ChatGetPaginatedDto, "tags"> & {
    limit?: ChatGetPaginatedDto["limit"];
    isActiveParticipant?: ChatGetPaginatedDto["isActiveParticipant"];
    hasMessages?: ChatGetPaginatedDto["hasMessages"];
  };
  displayProps?: Partial<typeof defaultDisplayProps>;
  propagatedDeps?: PropagatedDeps<ChatPaginatedListPropagatedDepsMap>;
  header?: ReactNode;
  onFetch?: () => void;
}

export type ChatPaginatedListProps = ChatPaginatedListOwnProps;

type ChatGetPaginatedInputDto = Omit<ChatGetPaginatedDto, "tags"> & {
  tags?: TagStaticFilterInputDto;
};

export default function ChatPaginatedList({
  persistenceKey,
  defaultValues,
  displayProps,
  propagatedDeps,
  header,
  onFetch,
}: ChatPaginatedListProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
    columns: {
      ...defaultDisplayProps.columns,
      ...displayProps?.columns,
    },
  };

  const currentTenant = useCurrentTenant();
  const commonRequestParams = useCommonRequestParams<ChatGetPaginatedInputDto>({
    statePersistence: {
      isEnabled: !!persistenceKey,
      persistenceKey: persistenceKey || EntityType.Chat,
    },
    defaultValues: {
      limit: 10,
      params: {
        ...defaultValues,
      },
    },
    initParams: ({ filterDefinition: fd }) => {
      const tagFilter = fd?.items.find((x) => x.field === getTypedPath<ChatDto>().tags.$path);
      const typesFilter = fd?.items.find((x) => x.field === getTypedPath<ChatDto>().type.$path);
      const scopesFilter = fd?.items.find((x) => x.field === getTypedPath<ChatDto>().scope.$path);
      const vehicleFilter = fd?.items.find((x) => x.field === InternalChatColumns.Vehicle);
      const contractFilter = fd?.items.find((x) => x.field === InternalChatColumns.Contract);
      const assignmentFilter = fd?.items.find((x) => x.field === InternalChatColumns.Assignment);
      const damageDetectionFilter = fd?.items.find(
        (x) => x.field === InternalChatColumns.DamageDetection,
      );
      const damageCostEvaluationFilter = fd?.items.find(
        (x) => x.field === InternalChatColumns.DamageCostEvaluation,
      );
      const repairFilter = fd?.items.find((x) => x.field === InternalChatColumns.RepairOperation);
      const washFilter = fd?.items.find((x) => x.field === InternalChatColumns.Wash);
      const maintenanceFilter = fd?.items.find((x) => x.field === InternalChatColumns.Maintenance);
      const wheelOperationFilter = fd?.items.find(
        (x) => x.field === InternalChatColumns.WheelOperation,
      );

      const params: Partial<ChatGetPaginatedInputDto> = {
        tags: {
          operator: tagFilter?.operator,
          query: tagFilter?.value as GeneralAttachedTagsInputDto,
        } as TagStaticFilterInputDto,
      };

      if (typesFilter) {
        params.types = typesFilter.valueAsArrayOfEnumTyped("ChatType");
      }
      if (scopesFilter) {
        params.scopes = scopesFilter.valueAsArrayOfEnumTyped("GeneralScopeType");
      }
      if (vehicleFilter) {
        params.vehicleId = vehicleFilter.valueAsId();
      }
      if (contractFilter) {
        params.contractId = contractFilter.valueAsId();
      }
      if (assignmentFilter) {
        params.assignmentId = assignmentFilter.valueAsId();
      }
      if (damageDetectionFilter) {
        params.damageDetectionId = damageDetectionFilter.valueAsId();
      }
      if (damageCostEvaluationFilter) {
        params.damageCostEvaluationId = damageCostEvaluationFilter.valueAsId();
      }
      if (repairFilter) {
        params.repairId = repairFilter.valueAsId();
      }
      if (washFilter) {
        params.washId = washFilter.valueAsId();
      }
      if (maintenanceFilter) {
        params.maintenanceId = maintenanceFilter.valueAsId();
      }
      if (wheelOperationFilter) {
        params.wheelOperationId = wheelOperationFilter.valueAsId();
      }

      return params;
    },
    options: { useUrlSearchParamsToStoreSelectedPage: true },
  });

  const paginatedRequest = useApiRequest(
    apiClient.chatsApi.apiV1ChatsGetPost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      chatGetPaginatedDto: {
        ...commonRequestParams.params,
        offset: commonRequestParams.offset,
        limit: commonRequestParams.limit,
        search: commonRequestParams.search,
        tags:
          commonRequestParams.filterDefinition?.items
            .filter((x) => x.field === getTypedPath<ChatDto>().tags.$path)
            .map((x) => TagsHelper.mapFromFilter(x))[0] || undefined,
        sortDefinition: commonRequestParams.sortDefinitionDto,
        filterDefinition: commonRequestParams.filterDefinition,
        isActiveParticipant: defaultValues?.isActiveParticipant ?? true, // where active participant
        hasMessages: defaultValues?.hasMessages ?? true, // display non-empty chats
        isSortByUnreadCountDesc: true,
      },
    },
    {
      deps: [
        ...commonRequestParams.deps,
        commonRequestParams.filterDefinition,
        propagatedDeps?.depsMap["refetch"],
      ],
      debouncedDeps: {
        deps: [...commonRequestParams.debouncedDeps],
        wait: 500,
        options: { leading: false, trailing: true },
      },
      commonRequestParams: commonRequestParams,
      onRequestSuccess: onFetch,
    },
  );
  const paginated = paginatedRequest.data;

  const handleActiveChatUpdatedThrottled = useCallback(
    throttle(
      async (chatId: string) => {
        // We know that recently updated chat will be on the first page
        // so if we are not on the first page then do nothing
        // user will see the updated chat when he goes back to the first page
        if (paginated?.pagination?.currentPage !== 0) {
          return;
        }

        const chat = (
          await apiClient.chatsApi.apiV1ChatsChatIdGet({
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            chatId: chatId,
          })
        ).data;

        paginatedRequest.updateData((data) => {
          const items = chain([chat, ...(data.items || [])])
            .uniqBy((x) => x.id)
            .orderBy(
              [
                getTypedPath<ChatDto>().unread.unreadCount.$path,
                getTypedPath<ChatDto>().lastActivityAt.$path,
              ],
              ["desc", "desc"],
            )
            .value();
          if (items.length > (data.items?.length || 0)) {
            items.pop();
          }
          data.items = items;
        });
      },
      1000,
      { leading: false, trailing: true },
    ),
    [paginatedRequest, paginated],
  );
  const handleActiveChatUpdatedThrottledRef = useLatestRef(handleActiveChatUpdatedThrottled);

  const handleSemiActiveChatUpdatedDebounced = useCallback(
    debounce(
      async (chatId: string) => {
        // If we are not on the first page then do nothing because updated chat will be on the first page
        if (paginated?.pagination?.currentPage !== 0) {
          return;
        }

        // Otherwise we need to refetch the paginated list to get the updated first page
        paginatedRequest.refetch();
      },
      5000,
      { leading: false, trailing: true },
    ),
    [paginatedRequest, paginated],
  );
  const handleSemiActiveChatUpdatedDebouncedRef = useLatestRef(
    handleSemiActiveChatUpdatedDebounced,
  );

  // explicitly subscribe on any activities in any active chat for current tenant user
  useEffect(() => {
    const resultPromise = chatHubService.subscribeToChatActiveAnyActivityAnyMinimal((params) => {
      handleActiveChatUpdatedThrottledRef.current(params.chatId);
    });

    return () => {
      resultPromise.then((subscription) => {
        subscription?.unsubscribe();
      });
    };
  }, []);

  // explicitly subscribe on any activities in any semi-active chat for current tenant user
  useEffect(() => {
    const resultPromise = chatHubService.subscribeToChatSemiActiveAnyActivityAnyMinimal(
      (params) => {
        handleSemiActiveChatUpdatedDebouncedRef.current(params.chatId);
      },
    );

    return () => {
      resultPromise.then((subscription) => {
        subscription?.unsubscribe();
      });
    };
  }, []);

  useRealtimeDataUpdates({
    channelNames: [DataUpdatesChannelName.Entities(currentTenant?.id, EntityType.Chat)],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      paginatedRequest.handleEntityChanged(data);
    },
  });

  return (
    <ListViewLayout
      displayProps={{
        breadcrumbs: false,
        ...displayProps,
      }}
      header={displayProps?.header && (header || <SimpleViewPageHeader title='Chats' />)}
    >
      <DataTabular<ChatDto>
        columns={[
          {
            field: "unreadMessageCount",
            title: "Unread",
            description: "Unread messages in a chat",
            width: 40,
            if: featureManager.isEnabled(FeatureName.ChatMessageUnreadCounter),
            isVisible: true,
            isHideable: true,
            isSortable: false,
            isFilterable: false,
            renderCell: (item) => (
              <>
                <ChatMessageCountInTableCell
                  count={item.unread?.unreadCount ?? 0}
                  tooltipProps={{ title: "Unread messages" }}
                />
              </>
            ),
          },
          {
            field: getTypedPath<ChatDto>().id.$path,
            title: "ID",
            width: 250,
            if: displayProps.columns?.id,
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.id,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ChatDto>().globalNumber.$path,
            title: "Global number",
            if: displayProps.columns?.globalNumber,
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.globalNumber,
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ChatDto>().localNumber.$path,
            title: "Local number",
            if: displayProps.columns?.localNumber,
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.localNumber,
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: "from",
            title: "From",
            width: 250,
            isVisible: true,
            isFilterable: false,
            isSortable: false,
            isHideable: true,
            renderCell: (item) => {
              const other = item?.participants?.find((x) => x.id !== item?.currentParticipant?.id);
              return (
                <>
                  {!other && "-"}
                  {other && (
                    <Stack direction='row' spacing={1} sx={{ justifyContent: "flex-start" }}>
                      <ChatParticipantAvatar chatId={item.id!} participant={other} size={40} />
                      <Stack
                        direction='column'
                        justifyContent='center'
                        alignItems='flex-start'
                        spacing={0}
                      >
                        <AppTypography>{other?.personName?.name}</AppTypography>
                        {(item?.participants?.length || 0) > 2 && (
                          <Chip
                            label={`+${item.participants!.length - 2} participants`}
                            size='small'
                          />
                        )}
                        {(item?.participants?.length || 0) <= 2 && (
                          <AppTypography variant='caption'>{other?.email}</AppTypography>
                        )}
                      </Stack>
                    </Stack>
                  )}
                </>
              );
            },
          },
          {
            field: getTypedPath<ChatDto>().type.$path,
            title: "Type",
            width: 100,
            isVisible: true,
            isFilterable: true,
            isSortable: false,
            isHideable: true,
            renderCell: (item) => <InlineApiEnumValue type='ChatType' value={item.type} />,
            filters: {
              fieldType: FilterFieldType.Enum,
              isUseDefaultOperators: false,
              operators: [
                new FilterFieldOperatorSpec({
                  operator: FilterOperator.AnyOf,
                  filterType: FilterType.Static,
                  valueType: FilterValueType.ArrayOfEnum,
                  onChange: (newValue) => {
                    commonRequestParams.setOneParam2({
                      types: newValue.valueAsArrayOfEnumTyped("ChatType"),
                    });
                  },
                  valueInput: (props) => <FilterArrayOfEnumValueInput {...props} />,
                }),
              ],
              fieldTypeMeta: {
                enum: {
                  enumName: ApiEnumName.ChatType,
                  excludeValues: [ChatType.Assistant],
                },
              },
            },
          },
          {
            field: getTypedPath<ChatDto>().name.$path,
            title: "Name",
            width: 150,
            isVisible: true,
            isFilterable: false,
            isSortable: false,
            isHideable: true,
            renderCell: (item) => <AppTypography>{item.name}</AppTypography>,
          },
          {
            field: getTypedPath<ChatDto>().description.$path,
            title: "Description",
            width: 100,
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.description || "-",
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ChatDto>().scope.$path,
            title: "Scope",
            width: 200,
            isVisible: true,
            isFilterable: true,
            isSortable: false,
            isHideable: true,
            isToDisabled: true,
            renderCell: (item) => <GeneralScopeLink isFetch={false} scope={item.scope} />,
            filters: {
              fieldType: FilterFieldType.Enum,
              isUseDefaultOperators: false,
              operators: [
                new FilterFieldOperatorSpec({
                  operator: FilterOperator.AnyOf,
                  filterType: FilterType.Static,
                  valueType: FilterValueType.ArrayOfEnum,
                  onChange: (newValue) => {
                    commonRequestParams.setOneParam2({
                      scopes: newValue.valueAsArrayOfEnumTyped("GeneralScopeType"),
                    });
                  },
                  valueInput: (props) => <FilterArrayOfEnumValueInput {...props} />,
                }),
              ],
              fieldTypeMeta: {
                enum: {
                  enumName: ApiEnumName.GeneralScopeType,
                },
              },
            },
          },
          {
            field: getTypedPath<ChatDto>().lastMessage.$path,
            title: "Last message",
            width: 250,
            isVisible: true,
            isFilterable: false,
            isSortable: false,
            isHideable: true,
            renderCell: (item) => (
              <>
                {item.lastMessage && (
                  <AppTypography>
                    <i>{item.lastMessage ? item.lastMessage.body : "-"}</i>
                  </AppTypography>
                )}
              </>
            ),
          },
          {
            field: getTypedPath<ChatDto>().lastActivityAt.$path,
            title: "Last activity",
            width: 150,
            isVisible: true,
            isFilterable: true,
            isSortable: true,
            isHideable: true,
            renderCell: (item) => <Datetime datetime={item.lastActivityAt} />,
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: InternalChatColumns.Vehicle,
            title: "Vehicle",
            if: displayProps.columns?.vehicle,
            isVisible: false,
            isFilterable: true,
            renderCell: () => null,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseSingleFilter: true,
              isUseDefaultOperators: false,
              operators: [
                new FilterFieldOperatorSpec({
                  operator: FilterOperator.Equal,
                  filterType: FilterType.Dynamic,
                  valueType: FilterValueType.Id,
                  onChange: (newValue) => {
                    commonRequestParams.setManyParams({
                      vehicleId: newValue.valueAsId(),
                      contractId: undefined,
                      assignmentId: undefined,
                      damageDetectionId: undefined,
                      damageCostEvaluationId: undefined,
                      repairId: undefined,
                      washId: undefined,
                      maintenanceId: undefined,
                      wheelOperationId: undefined,
                    });
                  },
                  valueInput: (props) => (
                    <VehicleAutocomplete
                      {...props}
                      entityId={commonRequestParams.params?.vehicleId}
                      onChange={(newValue) => props.onChange(newValue?.id)}
                      size='small'
                    />
                  ),
                }),
              ],
            },
          },
          {
            field: InternalChatColumns.Contract,
            title: "Contract",
            if: displayProps.columns?.contract,
            isVisible: false,
            isFilterable: true,
            renderCell: () => null,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseSingleFilter: true,
              isUseDefaultOperators: false,
              operators: [
                new FilterFieldOperatorSpec({
                  operator: FilterOperator.Equal,
                  filterType: FilterType.Dynamic,
                  valueType: FilterValueType.Id,
                  onChange: (newValue) => {
                    commonRequestParams.setManyParams({
                      vehicleId: undefined,
                      contractId: newValue.valueAsId(),
                      assignmentId: undefined,
                      damageDetectionId: undefined,
                      damageCostEvaluationId: undefined,
                      repairId: undefined,
                      washId: undefined,
                      maintenanceId: undefined,
                      wheelOperationId: undefined,
                    });
                  },
                  valueInput: (props) => (
                    <ContractAutocomplete
                      {...props}
                      entityId={commonRequestParams.params?.contractId}
                      onChange={(newValue) => props.onChange(newValue?.id)}
                      size='small'
                    />
                  ),
                }),
              ],
            },
          },
          {
            field: InternalChatColumns.Assignment,
            title: "Assignment",
            if: displayProps.columns?.assignment,
            isVisible: false,
            isFilterable: true,
            renderCell: () => null,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseSingleFilter: true,
              isUseDefaultOperators: false,
              operators: [
                new FilterFieldOperatorSpec({
                  operator: FilterOperator.Equal,
                  filterType: FilterType.Dynamic,
                  valueType: FilterValueType.Id,
                  onChange: (newValue) => {
                    commonRequestParams.setManyParams({
                      vehicleId: undefined,
                      contractId: undefined,
                      assignmentId: newValue.valueAsId(),
                      damageDetectionId: undefined,
                      damageCostEvaluationId: undefined,
                      repairId: undefined,
                      washId: undefined,
                      maintenanceId: undefined,
                      wheelOperationId: undefined,
                    });
                  },
                  valueInput: (props) => (
                    <AssignmentAutocomplete
                      {...props}
                      entityId={commonRequestParams.params?.assignmentId}
                      onChange={(newValue) => props.onChange(newValue?.id)}
                      size='small'
                    />
                  ),
                }),
              ],
            },
          },
          {
            field: InternalChatColumns.DamageDetection,
            title: "Damage Detection",
            if: displayProps.columns?.damageDetection,
            isVisible: false,
            isFilterable: true,
            renderCell: () => null,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseSingleFilter: true,
              isUseDefaultOperators: false,
              operators: [
                new FilterFieldOperatorSpec({
                  operator: FilterOperator.Equal,
                  filterType: FilterType.Dynamic,
                  valueType: FilterValueType.Id,
                  onChange: (newValue) => {
                    commonRequestParams.setManyParams({
                      vehicleId: undefined,
                      contractId: undefined,
                      assignmentId: undefined,
                      damageDetectionId: newValue.valueAsId(),
                      damageCostEvaluationId: undefined,
                      repairId: undefined,
                      washId: undefined,
                      maintenanceId: undefined,
                      wheelOperationId: undefined,
                    });
                  },
                  valueInput: (props) => (
                    <DamageDetectionAutocomplete
                      {...props}
                      entityId={commonRequestParams.params?.damageDetectionId}
                      onChange={(newValue) => props.onChange(newValue?.id)}
                      size='small'
                    />
                  ),
                }),
              ],
            },
          },
          {
            field: InternalChatColumns.DamageCostEvaluation,
            title: "Damage Cost Evaluation",
            if: displayProps.columns?.damageCostEvaluation,
            isVisible: false,
            isFilterable: true,
            renderCell: () => null,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseSingleFilter: true,
              isUseDefaultOperators: false,
              operators: [
                new FilterFieldOperatorSpec({
                  operator: FilterOperator.Equal,
                  filterType: FilterType.Dynamic,
                  valueType: FilterValueType.Id,
                  onChange: (newValue) => {
                    commonRequestParams.setManyParams({
                      vehicleId: undefined,
                      contractId: undefined,
                      assignmentId: undefined,
                      damageDetectionId: undefined,
                      damageCostEvaluationId: newValue.valueAsId(),
                      repairId: undefined,
                      washId: undefined,
                      maintenanceId: undefined,
                      wheelOperationId: undefined,
                    });
                  },
                  valueInput: (props) => (
                    <DamageCostEvaluationAutocomplete
                      {...props}
                      entityId={commonRequestParams.params?.damageCostEvaluationId}
                      onChange={(newValue) => props.onChange(newValue?.id)}
                      size='small'
                    />
                  ),
                }),
              ],
            },
          },
          {
            field: InternalChatColumns.RepairOperation,
            title: "Repair Operation",
            if: displayProps.columns?.repairOperation,
            isVisible: false,
            isFilterable: true,
            renderCell: () => null,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseSingleFilter: true,
              isUseDefaultOperators: false,
              operators: [
                new FilterFieldOperatorSpec({
                  operator: FilterOperator.Equal,
                  filterType: FilterType.Dynamic,
                  valueType: FilterValueType.Id,
                  onChange: (newValue) => {
                    commonRequestParams.setManyParams({
                      vehicleId: undefined,
                      contractId: undefined,
                      assignmentId: undefined,
                      damageDetectionId: undefined,
                      damageCostEvaluationId: undefined,
                      repairId: newValue.valueAsId(),
                      washId: undefined,
                      maintenanceId: undefined,
                      wheelOperationId: undefined,
                    });
                  },
                  valueInput: (props) => (
                    <RepairOperationAutocomplete
                      {...props}
                      entityId={commonRequestParams.params?.repairId}
                      onChange={(newValue) => props.onChange(newValue?.id)}
                      size='small'
                    />
                  ),
                }),
              ],
            },
          },
          {
            field: InternalChatColumns.Wash,
            title: "Wash",
            if: displayProps.columns?.wash,
            isVisible: false,
            isFilterable: true,
            renderCell: () => null,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseSingleFilter: true,
              isUseDefaultOperators: false,
              operators: [
                new FilterFieldOperatorSpec({
                  operator: FilterOperator.Equal,
                  filterType: FilterType.Dynamic,
                  valueType: FilterValueType.Id,
                  onChange: (newValue) => {
                    commonRequestParams.setManyParams({
                      vehicleId: undefined,
                      contractId: undefined,
                      assignmentId: undefined,
                      damageDetectionId: undefined,
                      damageCostEvaluationId: undefined,
                      repairId: undefined,
                      washId: newValue.valueAsId(),
                      maintenanceId: undefined,
                      wheelOperationId: undefined,
                    });
                  },
                  valueInput: (props) => (
                    <WashAutocomplete
                      {...props}
                      entityId={commonRequestParams.params?.washId}
                      onChange={(newValue) => props.onChange(newValue?.id)}
                      size='small'
                    />
                  ),
                }),
              ],
            },
          },
          {
            field: InternalChatColumns.Maintenance,
            title: "Maintenance",
            if: displayProps.columns?.maintenance,
            isVisible: false,
            isFilterable: true,
            renderCell: () => null,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseSingleFilter: true,
              isUseDefaultOperators: false,
              operators: [
                new FilterFieldOperatorSpec({
                  operator: FilterOperator.Equal,
                  filterType: FilterType.Dynamic,
                  valueType: FilterValueType.Id,
                  onChange: (newValue) => {
                    commonRequestParams.setManyParams({
                      vehicleId: undefined,
                      contractId: undefined,
                      assignmentId: undefined,
                      damageDetectionId: undefined,
                      damageCostEvaluationId: undefined,
                      repairId: undefined,
                      washId: undefined,
                      maintenanceId: newValue.valueAsId(),
                      wheelOperationId: undefined,
                    });
                  },
                  valueInput: (props) => (
                    <MaintenanceAutocomplete
                      {...props}
                      entityId={commonRequestParams.params?.maintenanceId}
                      onChange={(newValue) => props.onChange(newValue?.id)}
                      size='small'
                    />
                  ),
                }),
              ],
            },
          },
          {
            field: InternalChatColumns.WheelOperation,
            title: "Wheel Operation",
            if: displayProps.columns?.wheelOperation,
            isVisible: false,
            isFilterable: true,
            renderCell: () => null,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseSingleFilter: true,
              isUseDefaultOperators: false,
              operators: [
                new FilterFieldOperatorSpec({
                  operator: FilterOperator.Equal,
                  filterType: FilterType.Dynamic,
                  valueType: FilterValueType.Id,
                  onChange: (newValue) => {
                    commonRequestParams.setManyParams({
                      vehicleId: undefined,
                      contractId: undefined,
                      assignmentId: undefined,
                      damageDetectionId: undefined,
                      damageCostEvaluationId: undefined,
                      repairId: undefined,
                      washId: undefined,
                      maintenanceId: undefined,
                      wheelOperationId: newValue.valueAsId(),
                    });
                  },
                  valueInput: (props) => (
                    <WheelOperationAutocomplete
                      {...props}
                      entityId={commonRequestParams.params?.wheelOperationId}
                      onChange={(newValue) => props.onChange(newValue?.id)}
                      size='small'
                    />
                  ),
                }),
              ],
            },
          },
          {
            field: getTypedPath<ChatDto>().createdAt.$path,
            title: "Created at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.createdAt ? <Datetime datetime={item.createdAt} withDurationFromNow /> : "-",
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ChatDto>().updatedAt.$path,
            title: "Updated at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.updatedAt ? <Datetime datetime={item.updatedAt} withDurationFromNow /> : "-",
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          // {
          //   field: getTypedPath<ChatDto>().createdBy.$path,
          //   title: "Created by",
          //   isVisible: false,
          //   isHideable: true,
          //   isSortable: false,
          //   isFilterable: true,
          //   renderCell: (item) =>
          //     item.createdBy ? <InlineUser userId={item.createdBy} withAvatar /> : "-",
          //   filters: {
          //     fieldType: FilterFieldType.Id,
          //     operators: FilterCatalog.getOperatorsForIdFieldOfUser(),
          //   },
          // },
          // {
          //   field: getTypedPath<ChatDto>().updatedBy.$path,
          //   title: "Updated by",
          //   isVisible: false,
          //   isHideable: true,
          //   isSortable: false,
          //   isFilterable: true,
          //   renderCell: (item) =>
          //     item.updatedBy ? <InlineUser userId={item.updatedBy} withAvatar /> : "-",
          //   filters: {
          //     fieldType: FilterFieldType.Id,
          //     operators: FilterCatalog.getOperatorsForIdFieldOfUser(),
          //   },
          // },
          {
            field: getTypedPath<ChatDto>().participants.$path,
            title: "Participants",
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: false,
            isToDisabled: true,
            renderCell: (item) => (
              <TableCellContentOfCount count={item.participants?.length ?? 0} />
            ),
          },
          // hide tags because there might be T2T chats which must be handled correctly
          // {
          //   field: getTypedPath<ChatDto>().tags.$path,
          //   title: "Tags",
          //   isVisible: true,
          //   isHideable: true,
          //   isSortable: false,
          //   isFilterable: true,
          //   isColumnMenuDisabled: false,
          //   isToDisabled: true,
          //   renderCell: (item) => (
          //     <TableCellContentOfTags
          //       entityType={TagEntityType.Chat}
          //       entity={item}
          //       noDataPlaceholder='-'
          //       edit={{
          //         onSaved: (newValue) =>
          //           paginatedRequest.updateData((data) => {
          //             data.items?.forEach((item2) => {
          //               if (item2.id === item.id) {
          //                 item2.tags = newValue || undefined;
          //               }
          //             });
          //           }),
          //       }}
          //     />
          //   ),
          //   filters: {
          //     fieldType: FilterFieldType.Arbitrary,
          //     isUseSingleFilter: true,
          //     operators: FilterCatalog.getOperatorsForTagsField(),
          //   },
          // },
        ]}
        rows={paginated?.items || []}
        getRowId={(item) => item.id!}
        getRowIsHighlighted={(item) => (item.unread?.unreadCount || 0) > 0}
        rowTo={(row) => ROUTE_PATH.CHAT_VIEW(row.id!)}
        statePersistence={commonRequestParams.dataTabularProps.statePersistence}
        pagination={commonRequestParams.dataTabularProps.pagination}
        filters={commonRequestParams.dataTabularProps.filters}
        refetch={commonRequestParams.dataTabularProps.refetch}
        isColumnSortEnabled={false}
        isPaginationEnabled={true}
        isColumnFilterEnabled={displayProps.filters}
      />
    </ListViewLayout>
  );
}
